import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { FileText, Filter } from 'lucide-react';
import { toast } from 'sonner';

const AuditLog = () => {
  const { hasRole } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const canView = hasRole(['ADMIN', 'OWNER']);

  const fetchLogs = useCallback(async () => {
    try {
      const params = {};
      if (filter !== 'all') {
        params.entity_type = filter;
      }
      const response = await api.auditLogs.list(params);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (canView) {
      fetchLogs();
    } else {
      setLoading(false);
    }
  }, [canView, fetchLogs]);

  const getActionLed = (action) => {
    switch (action) {
      case 'CREATE':
        return (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-green bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
            <span className="led-indicator bg-led-green" /> CREATE
          </div>
        );
      case 'UPDATE':
        return (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-sky-400 bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
            <span className="led-indicator bg-sky-500" /> UPDATE
          </div>
        );
      case 'SOFT_DELETE':
        return (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-red bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
            <span className="led-indicator bg-led-red" /> SOFT DELETE
          </div>
        );
      case 'PASSWORD_RESET':
        return (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-amber bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
            <span className="led-indicator bg-led-amber" /> PASSWORD RESET
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-grey bg-industrial-s2 border border-industrial-border/60 px-2 py-0.5">
            <span className="led-indicator bg-led-grey" /> {action}
          </div>
        );
    }
  };

  if (!canView) {
    return (
      <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6">
        <div className="border-b border-industrial-border pb-4">
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Audit Log</h1>
          <p className="text-slate-400 mt-2 font-mono text-xs uppercase font-medium">System activity tracking</p>
        </div>
        <div className="bg-industrial-s1 border border-industrial-border p-8 text-center text-slate-500 font-mono text-xs uppercase">
          ACCESS DENIED: You do not have permissions to view system audit logs.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-industrial-border pb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Audit Log</h1>
          <p className="text-slate-400 mt-2 font-mono text-xs uppercase font-medium">System activity tracking</p>
        </div>
        <div className="flex items-center gap-2 bg-industrial-s1 border border-industrial-border px-3 py-1.5 w-fit">
          <Filter className="h-3.5 w-3.5 text-slate-400" />
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="h-8 w-40 rounded-none border border-industrial-border bg-industrial-s2 px-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
            data-testid="filter-select"
          >
            <option value="all">All Types</option>
            <option value="User">Users</option>
            <option value="Party">Parties</option>
            <option value="CoilIn">Coil Ins</option>
          </select>
        </div>
      </div>

      {/* History Grid Container */}
      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3 mb-4">
          <FileText className="h-5 w-5 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Activity History</h2>
        </div>

        {loading ? (
          <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">Loading audit entries...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">No audit logs found</div>
        ) : (
          <div className="space-y-3.5">
            {logs.map((log) => (
              <div
                key={log.id}
                className="border border-industrial-border bg-industrial-s2/25 p-4 hover:bg-industrial-s2/40 transition-colors font-mono text-xs"
                data-testid={`audit-log-${log.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      {getActionLed(log.action)}
                      
                      <span className="font-mono text-[9px] text-slate-400 border border-industrial-border bg-industrial-s2 px-2 py-0.5 uppercase">
                        {log.entity_type}
                      </span>
                      
                      <span className="text-[10px] text-slate-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    
                    <p className="text-sm font-sans text-white">
                      <span className="font-bold underline text-slate-200">{log.user_email}</span>
                      {' '}{log.action.toLowerCase().replace('_', ' ')} {log.entity_type.toLowerCase()}
                    </p>
                    
                    <p className="text-[10px] text-slate-500">
                      ENTITY ID: <span className="text-slate-400 font-bold">{log.entity_id}</span>
                    </p>
                    
                    {(log.before_snapshot || log.after_snapshot) && (
                      <details className="mt-3 group">
                        <summary className="text-[11px] cursor-pointer text-accent hover:text-accent-hover font-semibold select-none list-none flex items-center gap-1">
                          <span className="transition-transform group-open:rotate-90">▶</span> VIEW DATA SNAPSHOT
                        </summary>
                        <div className="mt-2 p-3.5 bg-industrial-s2 border border-industrial-border/60 text-xs font-mono space-y-3.5">
                          {log.before_snapshot && (
                            <div>
                              <p className="font-sans uppercase text-[10px] text-slate-500 mb-1 font-bold">BEFORE SNAPSHOT:</p>
                              <pre className="whitespace-pre-wrap break-all bg-black/40 p-2.5 border border-industrial-border/40 text-slate-400">
                                {JSON.stringify(log.before_snapshot, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.after_snapshot && (
                            <div>
                              <p className="font-sans uppercase text-[10px] text-slate-500 mb-1 font-bold">AFTER SNAPSHOT:</p>
                              <pre className="whitespace-pre-wrap break-all bg-black/40 p-2.5 border border-industrial-border/40 text-slate-400">
                                {JSON.stringify(log.after_snapshot, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
